import { useState } from 'react';
import { SimpleSaveFormBuilder } from '@/formbuilder/SimpleSaveFormBuilder.page';
import { FORM_ERROR } from '@/formbuilder/formBuilder.constants';
import { MAX_NAME_LENGTH } from '@/main/app.constants';
import { useAuth } from '@/main/auth/hooks/useAuth.hook';
import { getPasswordErrorText, validPassword } from '@/utilities/password.utilities';
import type { FormElement } from '@/formbuilder/formBuilder.constants';

export type RegisterFormProps = {
  seeqDomain: any;
  domain: any;
  toggleDisplay: () => void;
};

export const RegisterForm = ({ seeqDomain, domain, toggleDisplay }: RegisterFormProps) => {
  const {
    updateFormData,
    formData: { firstName, lastName, username, password, passwordCheck },
    serverMessage,
    register,
  } = useAuth({ seeqDomain, domain });

  const [newUsernameFocused, setNewUsernameFocused] = useState(false);

  const formDefinition: FormElement[] = [
    {
      component: 'TextFieldFormComponent',
      name: 'firstName',
      testId: 'firstName',
      label: 'USER.FIRST_NAME',
      placeholder: 'USER.FIRST_NAME_PROMPT',
      value: firstName,
      onChange: (firstName) => updateFormData({ firstName: firstName as string }),
      size: 'sm',
      autoComplete: 'given-name',
      maxLength: MAX_NAME_LENGTH.NAME,
      required: true,
      customErrorText: firstName.length > MAX_NAME_LENGTH.NAME ? 'NAME_LENGTH_ERROR' : '',
      customErrorParams: { count: MAX_NAME_LENGTH.NAME },
      autoFocus: true,
    },
    {
      component: 'TextFieldFormComponent',
      name: 'lastName',
      testId: 'lastName',
      label: 'USER.LAST_NAME',
      placeholder: 'USER.LAST_NAME_PROMPT',
      value: lastName,
      onChange: (lastName) => updateFormData({ lastName: lastName as string }),
      size: 'sm',
      autoComplete: 'family-name',
      maxLength: MAX_NAME_LENGTH.NAME,
      required: true,
      customErrorText: lastName.length > MAX_NAME_LENGTH.NAME ? 'NAME_LENGTH_ERROR' : '',
      customErrorParams: { count: MAX_NAME_LENGTH.NAME },
    },
    {
      component: 'TextFieldFormComponent',
      name: 'newUsername',
      testId: 'newUsername',
      label: 'USER.EMAIL',
      placeholder: 'USER.EMAIL_PROMPT',
      type: 'email',
      value: username,
      onChange: (username) => updateFormData({ username: username as string }),
      size: 'sm',
      autoComplete: 'username',
      onFocus: () => setNewUsernameFocused(true),
      onBlur: () => setNewUsernameFocused(false),
      required: true,
      customErrorText: newUsernameFocused ? 'USER.EMAIL_INVALID' : '',
    },
    {
      component: 'TextFieldFormComponent',
      name: 'password',
      testId: 'password',
      label: 'USER.PASSWORD',
      placeholder: 'USER.PASSWORD_PROMPT',
      type: 'password',
      value: password,
      onChange: (password) => updateFormData({ password: password as string }),
      size: 'sm',
      autoComplete: 'new-password',
      validation: (password: string | number) => !validPassword(password as string),
      required: true,
      customErrorText: getPasswordErrorText(password),
    },
    {
      component: 'TextFieldFormComponent',
      name: 'passwordCheck',
      testId: 'passwordCheck',
      label: 'USER.PASSWORD_CHECK',
      placeholder: 'USER.PASSWORD_CHECK_PROMPT',
      type: 'password',
      value: passwordCheck,
      onChange: (passwordCheck) => updateFormData({ passwordCheck: passwordCheck as string }),
      size: 'sm',
      autoComplete: 'new-password',
      required: true,
      validation: (passwordCheck: string | number) => (passwordCheck as string) !== password,
      extendValidation: true,
      customErrorText: passwordCheck !== password ? 'USER.PASSWORD_MISMATCH' : '',
    },
    {
      component: 'ErrorMessageFormComponent',
      name: 'serverErrorMessage',
      testId: 'specServerErrorMessage',
      type: FORM_ERROR,
      value: serverMessage,
      failForm: false,
      includeIf: !!serverMessage,
    },
  ];

  return (
    <SimpleSaveFormBuilder
      testId="register"
      formDefinition={formDefinition}
      submitFn={register}
      closeFn={toggleDisplay}
      buttonExtraClassNames="mt25 loginSubmitBtnWrapper"
      submitBtnLabel="LOGIN_PANEL.REGISTER"
      cancelClassNames="mr20 min-width-100"
    />
  );
};
